/**
 * Générateur — Attendu 950 — Calculer l'aire du disque, le volume du cylindre
 * de révolution.
 * Niveau : 5ème | Domaine : Espace et géométrie / Grandeurs : aires, volumes et unités
 *
 * Plan de cartes (version alignée sur la spécification 950 du 2026-09-21) :
 *
 *   Carte « Aire d'un disque » — la donnée alterne entre le RAYON et le
 *     DIAMÈTRE, le disque est dessiné à l'échelle de la donnée et la cote est
 *     tracée en pointillés. Trois formats : valeur approchée seule (niveaux 1
 *     et 2), valeur exacte ET valeur approchée (niveau 3), QCM.
 *   Carte « Quart, demi et trois quarts de disque » — niveau 4, angles
 *     multiples de 90 degrés uniquement.
 *   Carte « Aire d'une figure composée » — un rectangle ou un carré et des
 *     demi-disques : toutes les formules employées sont au programme.
 *   Carte « Volume d'un cylindre » — niveaux 5 et 6, le solide est représenté
 *     en perspective cavalière avec sa base cotée.
 *   Carte « Aires et volumes : mélange » — niveau 7.
 *
 * ── Spécification 950 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   calculs_concernes   liste fermée : aire du disque, volume du cylindre
 *   rayon_du_disque     2 à 10, à une décimale ; rayon_du_cylindre 2 à 10, entier
 *   hauteur_du_cylindre 2 à 15, jamais égale au rayon ni au diamètre
 *   donnee_de_la_base   rayon ou diamètre, en alternance équilibrée
 *   valeur_de_pi        EXACTE dans le calcul, l'approximation vient en dernier
 *   precision_de_la_valeur_approchee 0,1 pour une aire, 1 pour un volume
 *   largeur_de_l_encadrement_accepte = la précision : troncature ET troncature
 *                       augmentée d'un pas sont toutes deux acceptées
 *   nombre_de_valeurs_demandees_par_question 1 ou 2
 *   angle_de_la_portion_de_disque 90, 180 ou 270 degrés
 *
 * Exclusions appliquées : le périmètre du cercle n'est jamais demandé ; aucune
 * figure composée avec une formule hors programme ; la valeur 3,14 n'est JAMAIS
 * substituée à pi dans un calcul ; deux cotes d'une même figure ne portent
 * jamais le même nombre ; aucune portion d'angle non multiple de 90 ; toute
 * figure est dessinée à l'échelle de la donnée, sauf la figure composée, dont
 * l'énoncé dit qu'elle n'est qu'un schéma.
 *
 * Erreurs visées → mots-clés :
 *   Appliquer la formule de l'aire au diamètre         → diametre_pour_rayon_aire
 *   Prendre le diamètre pour le rayon (cylindre)       → diametre_pour_rayon_volume
 *   Confondre l'aire du disque et la longueur du cercle → aire_pour_perimetre
 *   Remplacer pi par 3,14 quand l'exact est demandé    → pi_approche_pour_exact
 *   Écrire une égalité là où il faut une approximation → egalite_pour_approximation
 *   Confondre l'unité d'une aire, d'une longueur, d'un volume → unite_confondue
 *   Oublier que le contour d'une portion comporte ses rayons → contour_portion_incomplet
 *
 * Graduation → cartes :
 *   niveau 1 (aire, rayon donné, valeur approchée)   aireDisque_saisie
 *   niveau 2 (aire, diamètre donné)                  aireDisque_saisie
 *   niveau 3 (valeur exacte ET valeur approchée)     aireDisque_texte_trou_saisie
 *   niveau 4 (quart, demi, trois quarts)             portion_*
 *   niveau 5 (volume, rayon donné)                   volCylindre_*
 *   niveau 6 (volume, diamètre donné)                volCylindre_*
 *   niveau 7 (mélange)                               melange_qcm_unique
 *   réglage : précision au dixième (aire) ou à l'unité (volume)
 *   réglage : figure représentée (toujours, sauf la figure composée)
 *
 * PI EXACT : tout part de la valeur exacte de pi ; l'approximation n'intervient
 * qu'à la dernière ligne, et l'intervalle accepté (troncature, troncature + un
 * pas) est calculé depuis cette valeur exacte, jamais depuis un arrondi
 * intermédiaire (vigilance 1 et 2, exclusion 3).
 */

import {
    randInt, randomChoice, makeSaisie, makeTexteTrou, makeQcm,
} from '../../../utils';
import { svgAireComposee } from './svgSolides';

const ATTENDU_ID = 950;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_AIRE = 'Aire d’un disque';
const L_PORTION = 'Quart, demi et trois quarts de disque';
const L_COMP = 'Aire d’une figure composée';
const L_VOL = 'Volume d’un cylindre';
const L_MEL = 'Aires et volumes : mélange';

const salt = () => randInt(0, 999999);

// ─── Écriture des nombres ────────────────────────────────────────────────────

/** Écriture française exacte d'un entier de centièmes (jamais de flottant). */
const frC = (c) => {
    const s = c < 0 ? '-' : '';
    const a = Math.abs(c);
    const ent = Math.floor(a / 100);
    const frac = String(a % 100).padStart(2, '0').replace(/0+$/, '');
    return s + (frac ? `${ent},${frac}` : String(ent));
};
/** Le coefficient de pi, écrit devant le symbole (« 20,25 π », « π » si 1). */
const coeffPi = (c) => (c === 100 ? 'π' : `${frC(c)} π`);

/**
 * Les deux valeurs approchées recevables d'une valeur EXACTE : sa troncature à
 * la précision demandée et cette troncature augmentée d'un pas (vigilance 1).
 * `pas` vaut 10 pour le dixième, 100 pour l'unité, en centièmes.
 */
const encadrement = (exacte, pas) => {
    const t = Math.floor((exacte * 100) / pas + 1e-9) * pas;    // troncature depuis la valeur EXACTE
    return [t, t + pas];
};
/**
 * L'ARRONDI de la valeur exacte à la précision demandée, en centièmes.
 *
 * `encadrement` renvoie un encadrement, pas un arrondi. Les cartes affichaient
 * `bornes[1]`, la borne HAUTE : dès que la valeur exacte tombe sous la moitié du
 * pas — une fois sur deux — la valeur attendue n'était pas l'arrondi. Mesuré sur
 * 1 652 cartes : 52,7 % de valeurs attendues fausses. Trois quarts d'un disque de
 * rayon 4,4 cm : 14,52 π = 45,6159…, la carte attendait 45,7 au lieu de 45,6
 * (retour prof 2026-09-26). L'encadrement reste la RÈGLE D'ACCEPTATION de la
 * spec (troncature et arrondi supérieur tous les deux recevables) ; l'arrondi est
 * la valeur AFFICHÉE, et il appartient toujours à cet encadrement.
 */
const arrondiC = (exacte, pas) => Math.round((exacte * 100) / pas) * pas;
/** Écritures admises d'une valeur approchée avec son unité (type_reponse). */
const ecrituresApprochee = (bornes, unite) => {
    const out = [];
    bornes.forEach((c) => ['²', '2', '^2', '³', '3', '^3'].forEach((e) => {
        if ((unite === 'cm²' && '²2^2'.includes(e)) || (unite === 'cm³' && '³3^3'.includes(e))) {
            out.push(`${frC(c)} cm${e}`);
        }
    }));
    return [...new Set(out)];
};

// ─── Figures, toutes à l'échelle de la donnée (exclusion 6) ──────────────────

const PX = 11;                              // pixels par centimètre, identique partout
const NOIR = '#1f2937';
const R1 = (v) => Number(v).toFixed(1);

/**
 * Disque dessiné à l'échelle du rayon tiré, centre marqué et nommé O, cote en
 * POINTILLÉS : du centre au bord pour un rayon, d'un bord à l'autre pour un
 * diamètre (objets_geometriques 1 et 2).
 */
const svgDisqueCote = (rDix, donnee) => {
    const rpx = (rDix / 10) * PX;
    const W = Math.round(2 * rpx + 74), H = Math.round(2 * rpx + 54);
    const cx = W / 2, cy = H / 2;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    out.push(`<circle cx="${R1(cx)}" cy="${R1(cy)}" r="${R1(rpx)}" fill="#dbeafe" fill-opacity="0.55" stroke="#1E3A8A" stroke-width="2"/>`);
    const x1 = donnee === 'diametre' ? cx - rpx : cx;
    out.push(`<line x1="${R1(x1)}" y1="${R1(cy)}" x2="${R1(cx + rpx)}" y2="${R1(cy)}" stroke="${NOIR}" stroke-width="1.5" stroke-dasharray="5 4"/>`);
    out.push(`<circle cx="${R1(cx)}" cy="${R1(cy)}" r="2.4" fill="${NOIR}"/>`);
    out.push(`<text x="${R1(cx - 6)}" y="${R1(cy + 15)}" text-anchor="middle" font-size="13" fill="${NOIR}">O</text>`);
    const val = donnee === 'diametre' ? frC(2 * rDix * 10) : frC(rDix * 10);
    // Le libelle etait pose au-dessus de la cote, DANS le disque : « R = 6,2 cm » fait
    // une soixantaine de pixels et son bout venait mordre le cercle (mesure : 0,1 px,
    // retour prof 2026-09-26) — et sur un petit disque il le traversait de part en
    // part. On le pose au-dessus du disque entier, ou il est lisible a tout rayon.
    out.push(`<text x="${R1(cx)}" y="${R1(cy - rpx - 9)}" text-anchor="middle" font-size="12" font-weight="700" fill="${NOIR}">${donnee === 'diametre' ? 'D' : 'R'} = ${val} cm</text>`);
    out.push('</svg>');
    return out.join('');
};

/** Distance d'un point au segment [S, U]. */
const distSegment = (P, S, U) => {
    const vx = U[0] - S[0], vy = U[1] - S[1];
    const L2 = vx * vx + vy * vy;
    const t = L2 ? Math.max(0, Math.min(1, ((P[0] - S[0]) * vx + (P[1] - S[1]) * vy) / L2)) : 0;
    return Math.hypot(P[0] - (S[0] + t * vx), P[1] - (S[1] + t * vy));
};

/**
 * Place une lettre autour du point (cx, cy) : seize directions, trois
 * éloignements, on garde la position la plus éloignée de tous les `traits`. Le
 * texte est sondé sur ses deux bouts, pas seulement sur son centre — c'est son
 * BORD qui vient toucher un trait.
 */
const placerLettre = (cx, cy, txt, d0, traits) => {
    const demi = (String(txt).length * 13 * 0.55) / 2;
    let best = null;
    [d0, d0 + 5, d0 + 11].forEach((d) => {
        for (let i = 0; i < 16; i++) {
            const a = (i * Math.PI) / 8;
            const P = [cx + d * Math.cos(a), cy + d * Math.sin(a)];
            const sondes = [P, [P[0] - demi, P[1]], [P[0] + demi, P[1]]];
            const s = Math.min(...sondes.flatMap((S) => traits.map((g) => distSegment(S, g[0], g[1]))));
            if (!best || s > best.s + 0.01) best = { s, x: P[0], y: P[1] };
        }
    });
    return best;
};

/**
 * Portion de disque : quart, demi ou trois quarts, obtenue par rotation d'un
 * rayon. Les deux rayons qui ferment un quart ou un trois quarts portent des
 * marques d'égalité ; le demi-disque est fermé par un diamètre et n'en porte
 * pas (objets_geometriques 3).
 */
const svgPortion = (rDix, angle) => {
    const rpx = (rDix / 10) * PX;
    const W = Math.round(2 * rpx + 70), H = Math.round(2 * rpx + 56);
    const cx = W / 2, cy = H / 2;
    const p = (a) => [cx + rpx * Math.cos((a * Math.PI) / 180), cy - rpx * Math.sin((a * Math.PI) / 180)];
    const [x1, y1] = p(0), [x2, y2] = p(angle);
    const grand = angle > 180 ? 1 : 0;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    out.push(`<path d="M ${R1(cx)} ${R1(cy)} L ${R1(x1)} ${R1(y1)} A ${R1(rpx)} ${R1(rpx)} 0 ${grand} 0 ${R1(x2)} ${R1(y2)} Z" fill="#dbeafe" fill-opacity="0.55" stroke="#1E3A8A" stroke-width="2"/>`);
    // Tous les traits proches du centre, pour y placer la lettre O sans la poser dessus.
    const traits = [[[cx, cy], [x1, y1]], [[cx, cy], [x2, y2]]];
    if (angle !== 180) {
        // marques d'égalité au milieu de chaque rayon
        [[x1, y1], [x2, y2]].forEach(([x, y]) => {
            const mx = (cx + x) / 2, my = (cy + y) / 2;
            const dx = (y - cy) / (2 * rpx) * 9, dy = -(x - cx) / (2 * rpx) * 9;
            traits.push([[mx - dx, my - dy], [mx + dx, my + dy]]);
            out.push(`<line x1="${R1(mx - dx)}" y1="${R1(my - dy)}" x2="${R1(mx + dx)}" y2="${R1(my + dy)}" stroke="#b45309" stroke-width="2"/>`);
        });
    }
    out.push(`<circle cx="${R1(cx)}" cy="${R1(cy)}" r="2.4" fill="${NOIR}"/>`);
    // La lettre O etait posee en bas a gauche du centre, dans une direction FIXE : sur
    // un trois quarts de petit rayon elle tombait pile sur la marque d'egalite du rayon
    // vertical (mesure : 0,0 px, retour prof 2026-09-26). On sonde seize directions et
    // trois eloignements, et on garde la place la plus degagee de tous les traits.
    const O = placerLettre(cx, cy, 'O', 13, traits);
    out.push(`<text x="${R1(O.x)}" y="${R1(O.y + 4)}" text-anchor="middle" font-size="13" fill="${NOIR}">O</text>`);
    out.push(`<line x1="${R1(cx)}" y1="${R1(cy)}" x2="${R1(x1)}" y2="${R1(y1)}" stroke="${NOIR}" stroke-width="1.5" stroke-dasharray="5 4"/>`);
    out.push(`<text x="${R1((cx + x1) / 2)}" y="${R1(cy - 7)}" text-anchor="middle" font-size="12" font-weight="700" fill="${NOIR}">R = ${frC(rDix * 10)} cm</text>`);
    out.push('</svg>');
    return out.join('');
};

/**
 * Cylindre de révolution en perspective cavalière, à l'échelle : base cotée par
 * un rayon ou un diamètre en POINTILLÉS, hauteur cotée à l'extérieur
 * (objets_geometriques 4).
 */
const svgCylindreCote = (r, h, donnee) => {
    // L'aplatissement du disque du haut est une CONVENTION de tracé, pas une longueur
    // mesurée : le rayon reste à l'échelle (rpx = r × PX), seule la fuite change. À
    // 0,32 le disque s'écrasait et le libellé « D = 6 cm » tombait sur l'arc (retour
    // prof 2026-09-26). 0,42 est l'ouverture retenue partout ailleurs dans le projet.
    const rpx = r * PX, ry = rpx * 0.42, hpx = h * PX;
    // Deux marges DISTINCTES. À droite, la cote de hauteur et son libellé « h = 15 cm »
    // demandent une centaine de pixels ; le centre se calculait à partir de la largeur
    // totale, si bien qu'élargir le cadre déplaçait le cylindre d'autant et le libellé
    // restait dehors. Il sortait du cadre d'une vingtaine de pixels (audit du 2026-09-24).
    const MARGE_G = 34;
    const MARGE_D = 104;
    // MARGE_H loge le libellé de la base, posé AU-DESSUS de tout le disque : sa
    // demi-hauteur de texte plus son décalage.
    const MARGE_H = 30;
    const W = Math.round(MARGE_G + 2 * rpx + MARGE_D), H = Math.round(hpx + 2 * ry + 34 + MARGE_H);
    const cx = MARGE_G + rpx;
    const yh = MARGE_H + ry, yb = yh + hpx;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`];
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);
    out.push(`<line x1="${R1(cx - rpx)}" y1="${R1(yh)}" x2="${R1(cx - rpx)}" y2="${R1(yb)}" stroke="#1E3A8A" stroke-width="2"/>`);
    out.push(`<line x1="${R1(cx + rpx)}" y1="${R1(yh)}" x2="${R1(cx + rpx)}" y2="${R1(yb)}" stroke="#1E3A8A" stroke-width="2"/>`);
    // base du bas : demi avant plein, demi arrière en pointillés
    out.push(`<path d="M ${R1(cx - rpx)} ${R1(yb)} A ${R1(rpx)} ${R1(ry)} 0 0 0 ${R1(cx + rpx)} ${R1(yb)}" fill="none" stroke="#1E3A8A" stroke-width="2"/>`);
    out.push(`<path d="M ${R1(cx - rpx)} ${R1(yb)} A ${R1(rpx)} ${R1(ry)} 0 0 1 ${R1(cx + rpx)} ${R1(yb)}" fill="none" stroke="#1E3A8A" stroke-width="2" stroke-dasharray="5 4"/>`);
    out.push(`<ellipse cx="${R1(cx)}" cy="${R1(yh)}" rx="${R1(rpx)}" ry="${R1(ry)}" fill="#dbeafe" fill-opacity="0.5" stroke="#1E3A8A" stroke-width="2"/>`);
    // cote de la base, en pointillés
    const x1 = donnee === 'diametre' ? cx - rpx : cx;
    out.push(`<line x1="${R1(x1)}" y1="${R1(yh)}" x2="${R1(cx + rpx)}" y2="${R1(yh)}" stroke="${NOIR}" stroke-width="1.5" stroke-dasharray="5 4"/>`);
    out.push(`<circle cx="${R1(cx)}" cy="${R1(yh)}" r="2.2" fill="${NOIR}"/>`);
    // Au-dessus de TOUT le disque : a 8 px de la cote, le libelle tombait sur l'arc.
    out.push(`<text x="${R1((x1 + cx + rpx) / 2)}" y="${R1(yh - ry - 8)}" text-anchor="middle" font-size="12" font-weight="700" fill="${NOIR}">${donnee === 'diametre' ? 'D' : 'R'} = ${donnee === 'diametre' ? 2 * r : r} cm</text>`);
    // cote de la hauteur, à l'extérieur
    const xh = cx + rpx + 18;
    out.push(`<line x1="${R1(xh)}" y1="${R1(yh)}" x2="${R1(xh)}" y2="${R1(yb)}" stroke="${NOIR}" stroke-width="1.2"/>`);
    out.push(`<line x1="${R1(xh - 4)}" y1="${R1(yh)}" x2="${R1(xh + 4)}" y2="${R1(yh)}" stroke="${NOIR}" stroke-width="1.2"/>`);
    out.push(`<line x1="${R1(xh - 4)}" y1="${R1(yb)}" x2="${R1(xh + 4)}" y2="${R1(yb)}" stroke="${NOIR}" stroke-width="1.2"/>`);
    out.push(`<text x="${R1(xh + 6)}" y="${R1((yh + yb) / 2 + 4)}" font-size="12" font-weight="700" fill="${NOIR}">h = ${h} cm</text>`);
    out.push('</svg>');
    return out.join('');
};

const figEnonce = (svg, alt, maxWidth = 'small') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth, alt } }],
});

// ─── Tirages ─────────────────────────────────────────────────────────────────

/** Rayon du disque : 2 à 10 cm, à une décimale (en DIXIÈMES entiers). */
const tirerDisque = () => {
    const rDix = randInt(20, 100);
    const donnee = randomChoice(['rayon', 'diametre']);
    return { rDix, donnee, coeff: rDix * rDix };             // coefficient de pi, en CENTIÈMES (R² × 100)
};

/** Cylindre : rayon entier 2 à 10, hauteur entière 2 à 15, jamais égale au rayon ni au diamètre. */
const tirerCylindre = () => {
    for (let essai = 0; essai < 80; essai++) {
        const r = randInt(2, 10), h = randInt(2, 15);
        if (h !== r && h !== 2 * r) return { r, h, donnee: randomChoice(['rayon', 'diametre']) };
    }
    return { r: 3, h: 11, donnee: 'rayon' };
};

/**
 * Portion : angle multiple de 90 (exclusion 5), rayon de 2 à 10 cm par pas de
 * 0,2 cm — un nombre PAIR de dixièmes, condition pour que le coefficient de pi
 * reste un décimal à deux chiffres après la virgule.
 */
const tirerPortion = () => ({
    rDix: 2 * randInt(10, 50),
    angle: randomChoice([90, 180, 270]),
});
// Groupes nominaux accordés, élision comprise : « l'aire des trois quarts d'un
// disque », jamais « l'aire d'trois quarts de disque ».
const NOM_PORTION = { 90: 'un quart de disque', 180: 'un demi-disque', 270: 'les trois quarts d’un disque' };
const DE_PORTION = { 90: 'd’un quart de disque', 180: 'd’un demi-disque', 270: 'des trois quarts d’un disque' };

const DONNEE_PHRASE = (donnee, valeur) => (donnee === 'diametre'
    ? `de <b>diamètre ${valeur} cm</b>`
    : `de <b>rayon ${valeur} cm</b>`);

const RAPPEL_AIRE = 'Rappel : l’aire d’un disque de rayon R vaut A = π × R × R.';
const RAPPEL_VOL = 'Rappel : le volume d’un cylindre vaut V = B × h, où B = π × R × R est l’aire de sa base.';

// ═══════════════════════════════════════════════════════════
// Carte — Aire d'un disque
// ═══════════════════════════════════════════════════════════

/** Aire d'un disque : valeur approchée seule, au dixième (niveaux 1 et 2). */
const genAireSaisie = () => {
    const d = tirerDisque();
    const exacte = (d.coeff / 100) * Math.PI;
    const bornes = encadrement(exacte, 10);
    const approchee = arrondiC(exacte, 10);
    const valeur = d.donnee === 'diametre' ? frC(2 * d.rDix * 10) : frC(d.rDix * 10);
    const texte = `<p>Un disque a pour ${d.donnee === 'diametre' ? 'diamètre' : 'rayon'} `
        + `<b>${valeur} cm</b> (voir la figure).</p>`
        + '<p><b>Quelle est son aire, arrondie au dixième de cm² ?</b></p>'
        + `<p><i>${RAPPEL_AIRE} Une unité est attendue dans la réponse ; la troncature et `
        + 'l’arrondi supérieur au dixième sont tous les deux acceptés.</i></p>';
    const payload = makeSaisie(`${frC(approchee)} cm²`, texte, {
        ...opts(L_AIRE), suffixe: '',
        comparaison: 'expression', reponses_acceptees: ecrituresApprochee(bornes, 'cm²'),
        elements_additionnels: figEnonce(svgDisqueCote(d.rDix, d.donnee), 'Disque coté'),
    });
    payload._meta.params = {
        niveau: d.donnee === 'diametre' ? 'n2' : 'n1', objet: 'disque',
        rayonDixiemes: d.rDix, donnee: d.donnee, coeffCentiemes: d.coeff, bornes,
        erreursVisees: ['unite_confondue', 'egalite_pour_approximation'],
    };
    return payload;
};

/** Valeur exacte en multiple de pi ET valeur approchée (niveau 3). */
const genAireExacteApprochee = () => {
    const d = tirerDisque();
    const exacte = (d.coeff / 100) * Math.PI;
    const bornes = encadrement(exacte, 10);
    const valeur = d.donnee === 'diametre' ? frC(2 * d.rDix * 10) : frC(d.rDix * 10);
    const phrase = `Aire exacte, en multiple de π : {{${frC(d.coeff)}}} π cm²\n`
        + `Valeur approchée au dixième : {{${frC(arrondiC(exacte, 10))}}} cm²`;
    const texte = `<p>Un disque a pour ${d.donnee === 'diametre' ? 'diamètre' : 'rayon'} `
        + `<b>${valeur} cm</b> (voir la figure). <b>Complète.</b></p>`
        + `<p><i>${RAPPEL_AIRE} La valeur exacte garde le symbole π ; la valeur approchée est `
        + 'arrondie au dixième.</i></p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_AIRE), mode: 'saisie', strict: false,
        elements_additionnels: figEnonce(svgDisqueCote(d.rDix, d.donnee), 'Disque coté'),
    });
    payload._meta.params = {
        niveau: 'n3', objet: 'disque', rayonDixiemes: d.rDix, donnee: d.donnee,
        coeffCentiemes: d.coeff, bornes, valeurs: 2,
        erreursVisees: ['egalite_pour_approximation', 'pi_approche_pour_exact'],
    };
    return payload;
};

/** Construit le QCM de l'aire d'un disque (réutilisé par la carte mélange). */
const qcmAireDisque = (label) => {
    const d = tirerDisque();
    const exacte = (d.coeff / 100) * Math.PI;
    const bornes = encadrement(exacte, 10);
    const valeur = d.donnee === 'diametre' ? frC(2 * d.rDix * 10) : frC(d.rDix * 10);
    const correct = `${coeffPi(d.coeff)} cm²`;
    const distracteurs = [
        { code: 'diametre_pour_rayon_aire', valeur: `${coeffPi(4 * d.coeff)} cm²` },
        { code: 'aire_pour_perimetre', valeur: `${coeffPi(2 * d.rDix * 10)} cm` },
        { code: 'pi_approche_pour_exact', valeur: `${frC(arrondiC(exacte, 10))} cm²` },
    ];
    const texte = `<p>Un disque a pour ${d.donnee === 'diametre' ? 'diamètre' : 'rayon'} `
        + `<b>${valeur} cm</b> (voir la figure).</p>`
        + '<p><b>Quelle est son aire EXACTE ?</b></p>'
        + `<p><i>${RAPPEL_AIRE}</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((x) => x.valeur)], correct, texte, {
        ...opts(label), elements_additionnels: figEnonce(svgDisqueCote(d.rDix, d.donnee), 'Disque coté'),
    });
    payload._meta.params = {
        niveau: d.donnee === 'diametre' ? 'n2' : 'n1', objet: 'disque',
        rayonDixiemes: d.rDix, donnee: d.donnee, coeffCentiemes: d.coeff, bornes, distracteurs,
        erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Quart, demi et trois quarts de disque (niveau 4)
// ═══════════════════════════════════════════════════════════

const genPortionSaisie = () => {
    const p = tirerPortion();
    const coeff = (p.rDix * p.rDix * (p.angle / 90)) / 4;     // coefficient de pi, en centièmes
    const exacte = (coeff / 100) * Math.PI;
    const bornes = encadrement(exacte, 10);
    const phrase = `Aire exacte, en multiple de π : {{${frC(coeff)}}} π cm²\n`
        + `Valeur approchée au dixième : {{${frC(arrondiC(exacte, 10))}}} cm²`;
    const texte = `<p>La figure bleue représente <b>${NOM_PORTION[p.angle]}</b> de rayon <b>${frC(p.rDix * 10)} cm</b>. `
        + '<b>Complète.</b></p>'
        + `<p><i>${RAPPEL_AIRE} L’aire ${DE_PORTION[p.angle]} est la fraction correspondante `
        + 'de l’aire du disque entier.</i></p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_PORTION), mode: 'saisie', strict: false,
        elements_additionnels: figEnonce(svgPortion(p.rDix, p.angle), 'Portion de disque cotée'),
    });
    payload._meta.params = {
        niveau: 'n4', objet: 'portion', rayonDixiemes: p.rDix, donnee: 'rayon',
        angle: p.angle, coeffCentiemes: coeff, bornes, valeurs: 2,
        erreursVisees: ['egalite_pour_approximation'],
    };
    return payload;
};

const CONTOURS = {
    90: {
        correct: 'un arc de cercle et deux rayons',
        distracteurs: [
            { code: 'contour_portion_incomplet', valeur: 'un arc de cercle seulement' },
            { code: 'contour_portion_incomplet', valeur: 'un arc de cercle et un diamètre' },
            { code: 'contour_portion_incomplet', valeur: 'deux rayons seulement' },
        ],
    },
    180: {
        correct: 'un demi-cercle et un diamètre',
        distracteurs: [
            { code: 'contour_portion_incomplet', valeur: 'un demi-cercle seulement' },
            // « un demi-cercle et deux rayons » a ete retire : pour un DEMI-disque les
            // deux rayons sont alignes, ils FORMENT le diametre — l'option decrivait
            // donc exactement le meme contour que la bonne reponse et etait comptee
            // fausse (retour prof 2026-09-26). Le piege reste valable au quart et aux
            // trois quarts, ou les deux rayons sont perpendiculaires : « un arc et un
            // diametre » y est bien faux.
            { code: 'contour_portion_incomplet', valeur: 'un demi-cercle et un rayon' },
            { code: 'contour_portion_incomplet', valeur: 'un diamètre seulement' },
        ],
    },
    270: {
        correct: 'un arc de cercle et deux rayons',
        distracteurs: [
            { code: 'contour_portion_incomplet', valeur: 'un arc de cercle seulement' },
            { code: 'contour_portion_incomplet', valeur: 'un arc de cercle et un diamètre' },
            { code: 'contour_portion_incomplet', valeur: 'trois rayons' },
        ],
    },
};

const genPortionQcm = () => {
    const p = tirerPortion();
    const c = CONTOURS[p.angle];
    const texte = `<p>La figure bleue représente <b>${NOM_PORTION[p.angle]}</b> de rayon <b>${frC(p.rDix * 10)} cm</b>.</p>`
        + '<p><b>De quoi son contour est-il formé ?</b></p>';
    const payload = makeQcm([c.correct, ...c.distracteurs.map((x) => x.valeur)], c.correct, texte, {
        ...opts(L_PORTION), elements_additionnels: figEnonce(svgPortion(p.rDix, p.angle), 'Portion de disque cotée'),
    });
    payload._meta.params = {
        niveau: 'n4', objet: 'portion', rayonDixiemes: p.rDix, donnee: 'rayon',
        angle: p.angle, coeffCentiemes: (p.rDix * p.rDix * (p.angle / 90)) / 4,
        distracteurs: c.distracteurs, erreursVisees: ['contour_portion_incomplet'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Aire d'une figure composée
// ═══════════════════════════════════════════════════════════

const genAireComposee = () => {
    const type = randomChoice(['stade', 'demiCarre']);
    let V, W, intro, svg, r;
    if (type === 'stade') {
        const L = randInt(6, 15);
        r = randInt(2, 5);
        const h = 2 * r;
        V = L * h;                                  // le rectangle
        W = r * r * 100;                            // les deux demi-disques font un disque, en centièmes
        svg = svgAireComposee('stade', { Llabel: `${L} cm`, hLabel: `${h} cm`, salt: salt() }).svg;
        intro = `<p>Cette figure est un rectangle de <b>${L} cm</b> sur <b>${h} cm</b>, `
            + 'prolongé par un <b>demi-disque à chaque bout</b>.</p>';
    } else {
        const a = 2 * randInt(2, 7);
        r = a / 2;
        V = a * a;                                  // le carré
        W = (r * r * 100) / 2;                      // le demi-disque, en centièmes
        svg = svgAireComposee('demiCarre', { aLabel: `${a} cm`, salt: salt() }).svg;
        intro = `<p>Cette figure est un carré de côté <b>${a} cm</b> surmonté d'un `
            + '<b>demi-disque</b> dont le diamètre est le côté du carré.</p>';
    }
    const phrase = `Partie rectiligne : {{${V}}} cm²\nCoefficient de π : {{${frC(W)}}}`;
    const texte = `${intro}<p><b>Donne l'aire exacte sous la forme « nombre + nombre × π ».</b></p>`
        + '<p><i>Attention : ce dessin est un <b>schéma</b>, il n’est pas à l’échelle. '
        + 'Toutes les formules employées ici sont au programme : rectangle, carré, disque.</i></p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_COMP), mode: 'saisie', strict: false,
        elements_additionnels: figEnonce(svg, 'Schéma de la figure composée', 'medium'),
    });
    payload._meta.params = {
        niveau: 'n4', objet: 'composee', type, rayonDixiemes: r * 10, donnee: 'rayon',
        partieRectiligne: V, coeffCentiemes: W, valeurs: 2,
        erreursVisees: ['egalite_pour_approximation'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Volume d'un cylindre de révolution (niveaux 5 et 6)
// ═══════════════════════════════════════════════════════════

const genVolCylindre = () => {
    const c = tirerCylindre();
    const coeffBase = c.r * c.r * 100;              // aire de base, coefficient de pi (centièmes)
    const coeffVol = coeffBase * c.h;
    const exacte = (coeffVol / 100) * Math.PI;
    const bornes = encadrement(exacte, 100);        // précision à l'unité pour un volume
    const phrase = `Aire de la base, en multiple de π : {{${frC(coeffBase)}}} π cm²\n`
        + `Volume exact, en multiple de π : {{${frC(coeffVol)}}} π cm³\n`
        + `Volume approché à l'unité : {{${frC(arrondiC(exacte, 100))}}} cm³`;
    const texte = `<p>Un cylindre de révolution a une base ${DONNEE_PHRASE(c.donnee, c.donnee === 'diametre' ? 2 * c.r : c.r)} `
        + `et une <b>hauteur de ${c.h} cm</b> (voir la figure). <b>Complète.</b></p>`
        + `<p><i>${RAPPEL_VOL} Le volume approché est arrondi à l’unité.</i></p>`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_VOL), mode: 'saisie', strict: false,
        elements_additionnels: figEnonce(svgCylindreCote(c.r, c.h, c.donnee), 'Cylindre de révolution coté', 'medium'),
    });
    payload._meta.params = {
        niveau: c.donnee === 'diametre' ? 'n6' : 'n5', objet: 'cylindre',
        rayonDixiemes: c.r * 10, hauteur: c.h, donnee: c.donnee,
        coeffCentiemes: coeffVol, bornes, valeurs: 2,
        erreursVisees: ['egalite_pour_approximation', 'unite_confondue'],
    };
    return payload;
};

const qcmVolCylindre = (label) => {
    let c, coeffVol, correct, distracteurs;
    for (let essai = 0; essai < 60; essai++) {
        c = tirerCylindre();
        coeffVol = c.r * c.r * 100 * c.h;
        correct = `${coeffPi(coeffVol)} cm³`;
        distracteurs = [
            { code: 'diametre_pour_rayon_volume', valeur: `${coeffPi(4 * coeffVol)} cm³` },
            { code: 'aire_pour_perimetre', valeur: `${coeffPi(2 * c.r * c.h * 100)} cm³` },
            { code: 'unite_confondue', valeur: `${coeffPi(coeffVol)} cm²` },
        ];
        // rayon 2 : l'aire latérale coïncide avec le volume, le QCM perdrait
        // une proposition (jamais de bouche-trou, on retire un autre cylindre)
        if (new Set([correct, ...distracteurs.map((x) => x.valeur)]).size === 4) break;
    }
    const texte = `<p>Un cylindre de révolution a une base ${DONNEE_PHRASE(c.donnee, c.donnee === 'diametre' ? 2 * c.r : c.r)} `
        + `et une <b>hauteur de ${c.h} cm</b> (voir la figure).</p>`
        + '<p><b>Quel est son volume EXACT ?</b></p>'
        + `<p><i>${RAPPEL_VOL}</i></p>`;
    const payload = makeQcm([correct, ...distracteurs.map((x) => x.valeur)], correct, texte, {
        ...opts(label), elements_additionnels: figEnonce(svgCylindreCote(c.r, c.h, c.donnee), 'Cylindre de révolution coté', 'medium'),
    });
    payload._meta.params = {
        niveau: c.donnee === 'diametre' ? 'n6' : 'n5', objet: 'cylindre',
        rayonDixiemes: c.r * 10, hauteur: c.h, donnee: c.donnee,
        coeffCentiemes: coeffVol, distracteurs, erreursVisees: [],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte — Mélange (niveau 7)
// ═══════════════════════════════════════════════════════════

const genMelange = () => {
    const p = randomChoice([qcmAireDisque, qcmVolCylindre])(L_MEL);
    p._meta.params.niveau = 'n7';
    return p;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    aireDisque_saisie: genAireSaisie,
    aireDisque_texte_trou_saisie: genAireExacteApprochee,
    aireDisque_qcm_unique: () => qcmAireDisque(L_AIRE),
    portion_texte_trou_saisie: genPortionSaisie,
    portion_qcm_unique: genPortionQcm,
    aireComposee_texte_trou_saisie: genAireComposee,
    volCylindre_texte_trou_saisie: genVolCylindre,
    volCylindre_qcm_unique: () => qcmVolCylindre(L_VOL),
    melange_qcm_unique: genMelange,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie', saisie: 'Saisie' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'aireDisque', label: L_AIRE, formatsList: buildFormats('aireDisque') },
    { id: 'portion', label: L_PORTION, formatsList: buildFormats('portion') },
    { id: 'aireComposee', label: L_COMP, formatsList: buildFormats('aireComposee') },
    { id: 'volCylindre', label: L_VOL, formatsList: buildFormats('volCylindre') },
    { id: 'melange', label: L_MEL, formatsList: buildFormats('melange') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { frC, coeffPi, encadrement, ecrituresApprochee, tirerDisque, tirerCylindre };
